<template>
  <div
    class="create-workflow-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Create experiment"
    @click.self="$emit('close')"
  >
    <div class="create-workflow-modal">
      <div class="create-workflow-header">
        <h4>New Experiment</h4>
        <button type="button" class="workflow-modal-close-btn" @click="$emit('close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <form class="create-workflow-form" @submit.prevent="$emit('form-submit')">
        <div class="form-row">
          <div class="form-group">
            <label for="hierarchy-experiment-name">ID / Name *</label>
            <input
              id="hierarchy-experiment-name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.includes('name') }"
              :value="form.name"
              placeholder="e.g., EXP-001"
              @input="$emit('update-field', 'name', $event.target.value)"
            />
            <div v-if="errors.includes('name')" class="error-feedback">Name is required.</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="hierarchy-experiment-location">Location *</label>
            <template v-if="lockLocation">
              <input
                id="hierarchy-experiment-location"
                type="text"
                class="form-control"
                :value="fixedLocationName || resolvedLocationName || 'Fixed location'"
                disabled
              />
            </template>
            <select
              v-else
              id="hierarchy-experiment-location"
              class="form-control"
              :class="{ 'is-invalid': errors.includes('location_id') }"
              :value="form.location_id"
              @change="$emit('update-field', 'location_id', $event.target.value)"
            >
              <option disabled value="">Select a location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.name }}</option>
            </select>
            <div v-if="errors.includes('location_id')" class="error-feedback">Location is required.</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="hierarchy-experiment-start">Start Date</label>
            <input
              id="hierarchy-experiment-start"
              type="datetime-local"
              class="form-control"
              :value="form.start_date"
              @input="$emit('update-field', 'start_date', $event.target.value)"
            />
          </div>
          <div class="form-group">
            <label for="hierarchy-experiment-end">End Date</label>
            <input
              id="hierarchy-experiment-end"
              type="datetime-local"
              class="form-control"
              :value="form.end_date"
              @input="$emit('update-field', 'end_date', $event.target.value)"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Project (fixed)</label>
            <input type="text" class="form-control" :value="projectName || projectId" disabled />
          </div>
          <div class="form-group">
            <label>{{ contextLabel }} (fixed)</label>
            <input type="text" class="form-control" :value="contextName || actionName" disabled />
          </div>
        </div>

        <div class="create-workflow-actions">
          <button type="button" class="btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="isSubmitting">
            <span v-if="isSubmitting">Creating...</span>
            <span v-else>Create experiment</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HierarchyCreateExperimentModal',
  props: {
    projectName: {
      type: String,
      default: '',
    },
    projectId: {
      type: Number,
      required: true,
    },
    actionName: {
      type: String,
      default: '',
    },
    contextLabel: {
      type: String,
      default: 'Activity',
    },
    contextName: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      required: true,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    lockLocation: {
      type: Boolean,
      default: false,
    },
    fixedLocationName: {
      type: String,
      default: '',
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedLocationName() {
      const selected = this.locations.find((location) => Number(location.id) === Number(this.form.location_id));
      return selected?.name || '';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/variables.scss';

.create-workflow-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.create-workflow-modal {
  width: min(560px, 100%);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  padding: 24px;
}

.create-workflow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.workflow-modal-close-btn {
  border: 1px solid rgba($primary-color, 0.18);
  background: rgba(255, 255, 255, 0.92);
  color: $primary-color;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.create-workflow-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    color: $primary-color;
  }
}

.form-control {
  width: 100%;
  min-height: 42px;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: #222;
}

.form-control:disabled {
  background: #f3f5f8;
  color: #667085;
}

.form-control.is-invalid {
  border-color: #b00020;
}

.error-feedback {
  color: #b00020;
  font-size: 12px;
}

.create-workflow-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.btn-secondary {
  border: 1px solid rgba($primary-color, 0.2);
  background: #fff;
  color: $primary-color;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary {
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .create-workflow-modal {
    padding: 18px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
